import { useRef } from 'react';
import { useCellSelection, type UseCellSelectionReturn } from './useCellSelection';
import {
  copySelectionToClipboard,
  buildSelectionText,
} from '@/utils/cell-selection-utils';
import { useToastStore } from '@/stores/toast-store';

export interface UseCellSelectionWithCopyParams<T extends object> {
  colIds: string[];
  rowCountRef: React.MutableRefObject<number>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  rowHeight: number;
  headerHeight?: number;
  tableSelector: string;
  sortedDataRef: React.RefObject<T[]>;
  formatValue: (item: T, colId: string) => string;
  getHeader: (colId: string) => string;
}

export function useCellSelectionWithCopy<T extends object>(
  params: UseCellSelectionWithCopyParams<T>,
): UseCellSelectionReturn {
  const addToast = useToastStore((s) => s.addToast);
  const copyRef = useRef<(() => void) | undefined>(undefined);
  const copyWithHeadersRef = useRef<(() => void) | undefined>(undefined);

  const selection = useCellSelection({
    colIds: params.colIds,
    rowCountRef: params.rowCountRef,
    scrollContainerRef: params.scrollContainerRef,
    rowHeight: params.rowHeight,
    headerHeight: params.headerHeight,
    tableSelector: params.tableSelector,
    onCopy: () => copyRef.current?.(),
    onCopyWithHeaders: () => copyWithHeadersRef.current?.(),
  });

  copyRef.current = () => {
    const count = selection.getSelectedCellCount();
    copySelectionToClipboard(
      selection.getSelectedCellsAsSet(),
      params.colIds,
      selection.colIdToIndex,
      params.sortedDataRef,
      params.formatValue,
    );
    if (count > 0) addToast('success', `Copied ${count} cells`);
  };

  copyWithHeadersRef.current = () => {
    const text = buildSelectionText(
      selection.getSelectedCellsAsSet(),
      params.colIds,
      selection.colIdToIndex,
      params.sortedDataRef.current ?? [],
      {
        formatValue: params.formatValue,
        withHeaders: true,
        getHeader: params.getHeader,
      },
    );
    const count = selection.getSelectedCellCount();
    navigator.clipboard
      .writeText(text)
      .then(() => {
        if (count > 0) addToast('success', `Copied ${count} cells with headers`);
      })
      .catch(() => {});
  };

  return selection;
}
